// Admin-Freigabe-Warteschlange (Issue #187/#191): die offenen Registrierungen (Status
// pending_approval) freigeben oder ablehnen. Spricht nur den API-Client; das Backend erzwingt das
// admin-Recht serverseitig (401/403) — diese Ansicht spiegelt das nur in der UI. approve/reject
// sind nur aus pending_approval zulässig (sonst 409 not_pending); die Ansicht lädt danach neu.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import {
  ApiError,
  approveRegistration,
  listRegistrations,
  rejectRegistration,
} from '../api/client';
import { ROLES, type UserInfo } from '../api/types';
import { Button, EmptyState, PanelHeading } from '../components/ui';

// Eine Zeile der Warteschlange: das Konto plus die Freigabe-/Ablehnungsaktionen.
function RegistrationRow({
  user,
  onDecided,
}: {
  user: UserInfo;
  onDecided: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [roles, setRoles] = useState<string[]>(user.roles ?? []);
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleRole = (role: string) =>
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const approve = useCallback(async () => {
    setBusy(true);
    try {
      await approveRegistration(user.identity, roles.length ? roles : undefined);
      notify('success', t('registrations.approved', { identity: user.identity }));
      onDecided();
    } catch (err) {
      // 409 not_pending: das Konto wurde zwischenzeitlich schon entschieden — neu laden.
      if (err instanceof ApiError && err.status === 409) {
        notify('error', t('registrations.notPending'));
        onDecided();
      } else {
        reportError(err, t('registrations.approveFailed'));
      }
    } finally {
      setBusy(false);
    }
  }, [user.identity, roles, notify, reportError, onDecided, t]);

  const reject = useCallback(async () => {
    setBusy(true);
    try {
      await rejectRegistration(user.identity, reason.trim() || undefined);
      notify('success', t('registrations.rejected', { identity: user.identity }));
      onDecided();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        notify('error', t('registrations.notPending'));
        onDecided();
      } else {
        reportError(err, t('registrations.rejectFailed'));
      }
    } finally {
      setBusy(false);
    }
  }, [user.identity, reason, notify, reportError, onDecided, t]);

  return (
    <div className="rounded border border-bf-border bg-bf-navy p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-bf-white">{user.identity}</span>
        {user.email && <span className="text-xs text-bf-muted">{user.email}</span>}
      </div>
      {user.displayName && <p className="mt-0.5 text-xs text-bf-muted">{user.displayName}</p>}

      <div className="mt-2">
        <span className="mb-1 block text-xs text-bf-muted">{t('registrations.rolesLabel')}</span>
        <div className="flex flex-wrap gap-2">
          {ROLES.map((role) => {
            const on = roles.includes(role);
            return (
              <label
                key={role}
                className={
                  'flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-xs ' +
                  (on ? 'border-bf-gold text-bf-white' : 'border-bf-border text-bf-muted')
                }
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleRole(role)}
                  aria-label={t(`settings.role.${role}`)}
                  className="accent-bf-gold"
                />
                {t(`settings.role.${role}`)}
              </label>
            );
          })}
        </div>
      </div>

      {rejecting && (
        <label className="mt-2 block">
          <span className="mb-1 block text-xs text-bf-muted">{t('registrations.reason')}</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('registrations.reasonPlaceholder')}
            aria-label={t('registrations.reason')}
            className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
          />
        </label>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="primary"
          onClick={approve}
          disabled={busy}
          testId={`approve-${user.identity}`}
        >
          {t('registrations.approve')}
        </Button>
        {rejecting ? (
          <>
            <Button
              variant="danger"
              onClick={reject}
              disabled={busy}
              testId={`reject-confirm-${user.identity}`}
            >
              {t('registrations.rejectConfirm')}
            </Button>
            <Button variant="secondary" onClick={() => setRejecting(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            onClick={() => setRejecting(true)}
            disabled={busy}
            testId={`reject-${user.identity}`}
          >
            {t('registrations.reject')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function RegistrationsView(): JSX.Element {
  const { t } = useI18n();
  const { reportError } = useStore();
  const [pending, setPending] = useState<UserInfo[] | null>(null);

  const reload = useCallback(() => {
    listRegistrations('pending_approval')
      .then((res) => setPending(res.registrations))
      .catch((err) => {
        setPending([]);
        reportError(err, t('registrations.loadFailed'));
      });
  }, [reportError, t]);

  useEffect(reload, [reload]);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-2xl">
        <PanelHeading>{t('registrations.heading')}</PanelHeading>
        <p className="mb-4 text-sm text-bf-muted">{t('registrations.intro')}</p>

        {pending === null ? (
          <p className="text-sm text-bf-muted">{t('common.loading')}</p>
        ) : pending.length === 0 ? (
          <EmptyState title={t('registrations.empty')} hint={t('registrations.emptyHint')} />
        ) : (
          <div className="flex flex-col gap-3">
            {pending.map((u) => (
              <RegistrationRow key={u.identity} user={u} onDecided={reload} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
